<template>
  <div class="stage">
    <div ref="bgEl" class="stage__bg">
      <div class="stage__neb stage__neb--a" />
      <div class="stage__neb stage__neb--b" />
      <span
        v-for="(st, i) in STARS"
        :key="i"
        class="stage__star"
        :style="{ left: st.l, top: st.t, width: st.s, height: st.s, opacity: st.o }"
      />
    </div>

    <div ref="lightEl" class="stage__light" />
    <div class="stage__vignette" />

    <div class="stage__content">
      <slot />
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, onBeforeUnmount } from 'vue'
import { STARS } from './hubSky'

/**
 * O palco do Easy HUB: fundo quase preto, nebulosas, o céu compartilhado com a
 * antessala, vinheta e a luz que segue o cursor.
 *
 * As mesmas regras de desempenho da antessala valem aqui: o laço de quadro
 * NUNCA toca estado reativo do Vue. Posição de camada e da luz são escritas
 * direto no nó (el.style.transform), senão cada movimento do mouse viraria um
 * re-render dos filhos.
 */

const bgEl = ref<HTMLElement | null>(null)
const lightEl = ref<HTMLElement | null>(null)

const reduceMotion =
  typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches === true

// Alvo e posição suavizada do paralaxe, e a posição crua do cursor.
let tx = 0
let ty = 0
let mx = 0
let my = 0
let lx = 0
let ly = 0
let raf = 0
// Abaixo disso não há mouse para seguir: a luz não é montada e o laço não roda.
let wide = false

function onMove(e: MouseEvent) {
  if (!wide) return
  tx = (e.clientX / window.innerWidth - 0.5) * 2
  ty = (e.clientY / window.innerHeight - 0.5) * 2
  lx = e.clientX
  ly = e.clientY
  if (lightEl.value) lightEl.value.style.opacity = '1'
}

function onLeave() {
  tx = 0
  ty = 0
  if (lightEl.value) lightEl.value.style.opacity = '0'
}

function frame() {
  mx += (tx - mx) * 0.06
  my += (ty - my) * 0.06
  // Com movimento reduzido as camadas ficam paradas; a luz continua, porque é
  // resposta direta ao cursor e não animação própria da tela.
  const x = reduceMotion ? 0 : mx
  const y = reduceMotion ? 0 : my
  if (bgEl.value) bgEl.value.style.transform = `translate3d(${(-4 * x).toFixed(2)}px,${(-4 * y).toFixed(2)}px,0)`
  if (lightEl.value) lightEl.value.style.transform = `translate3d(${lx}px,${ly}px,0)`
  raf = requestAnimationFrame(frame)
}

function onResize() {
  wide = window.innerWidth >= 900
}

onMounted(() => {
  onResize()
  window.addEventListener('resize', onResize)
  window.addEventListener('mousemove', onMove)
  window.addEventListener('mouseout', onLeave)
  raf = requestAnimationFrame(frame)
})

onBeforeUnmount(() => {
  cancelAnimationFrame(raf)
  window.removeEventListener('resize', onResize)
  window.removeEventListener('mousemove', onMove)
  window.removeEventListener('mouseout', onLeave)
})
</script>

<style scoped lang="scss">
// Mesmos valores literais da antessala (src/pages/home/HomePage.vue).
.stage {
  position: fixed;
  inset: 0;
  background: #08080a;
  color: #e8e8e8;
  font-family: Inter, system-ui, sans-serif;
  -webkit-font-smoothing: antialiased;
  overflow: auto;

  &__bg {
    position: absolute;
    inset: 0;
    pointer-events: none;
    will-change: transform;
  }

  &__neb {
    position: absolute;
    left: 50%;
    top: 50%;

    &--a {
      width: 1700px;
      height: 1200px;
      transform: translate(-50%, -50%);
      background: radial-gradient(
        ellipse at center,
        rgba(120, 90, 110, 0.11),
        rgba(60, 40, 60, 0.05) 35%,
        transparent 60%
      );
    }

    &--b {
      width: 1000px;
      height: 800px;
      transform: translate(-32%, -30%);
      background: radial-gradient(ellipse at center, rgba(250, 43, 0, 0.07), transparent 55%);
    }
  }

  &__star {
    position: absolute;
    border-radius: 50%;
    background: #fff;
  }

  &__light {
    position: absolute;
    left: 0;
    top: 0;
    width: 900px;
    height: 900px;
    margin: -450px 0 0 -450px;
    border-radius: 50%;
    background: radial-gradient(circle, rgba(255, 255, 255, 0.05), rgba(250, 43, 0, 0.03) 35%, transparent 65%);
    pointer-events: none;
    opacity: 0;
    transition: opacity 0.6s;
    will-change: transform;

    // Sem cursor não há o que seguir, e um nó de 900x900 com will-change
    // pagaria camada de composição à toa.
    @media (max-width: 899px) {
      display: none;
    }
  }

  &__vignette {
    position: absolute;
    inset: 0;
    pointer-events: none;
    background: radial-gradient(ellipse at center, transparent 48%, rgba(0, 0, 0, 0.62) 100%);
  }

  &__content {
    position: relative;
    z-index: 1;
    min-height: 100%;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 32px 16px;
  }
}
</style>
